import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import api from '../../lib/api';
import {
  CreateLoadOptionPayload,
  UpdateLoadOptionPayload,
  CreatePalletSizePayload,
  UpdatePalletSizePayload,
} from './types';

export * from './types';

export const qk = {
  loadOptions: ['organization', 'load-options'] as const,
  palletSizes: ['organization', 'pallet-sizes'] as const,
};

/* ---------- Load Templates ---------- */
export function useListLoadOptions() {
  return useQuery({
    queryKey: qk.loadOptions,
    queryFn: async () => {
      const resp = await api.get('/organization/load-options');
      return resp.data?.loadOptions ?? resp.data ?? [];
    },
  });
}

export function useCreateLoadOption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateLoadOptionPayload) => {
      const resp = await api.post('/organization/load-options', payload);
      return resp.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.loadOptions });
      qc.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export function useUpdateLoadOption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: UpdateLoadOptionPayload) => {
      const resp = await api.put(`/organization/load-options/${id}`, data);
      return resp.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.loadOptions });
      qc.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export function useDeleteLoadOption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const resp = await api.delete(`/organization/load-options/${id}`);
      return resp.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.loadOptions });
      qc.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

/* ---------- Pallet Sizes ---------- */
export function useListPalletSizes() {
  return useQuery({
    queryKey: qk.palletSizes,
    queryFn: async () => {
      const resp = await api.get('/organization/pallet-sizes');
      return resp.data?.palletSizes ?? resp.data ?? [];
    },
  });
}

export function useCreatePalletSize() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreatePalletSizePayload) => {
      const resp = await api.post('/organization/pallet-sizes', payload);
      return resp.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.palletSizes });
      qc.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export function useUpdatePalletSize() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: UpdatePalletSizePayload) => {
      const resp = await api.put(`/organization/pallet-sizes/${id}`, data);
      return resp.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.palletSizes });
      qc.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export function useArchivePalletSize() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const resp = await api.delete(`/organization/pallet-sizes/${id}`);
      return resp.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.palletSizes });
      qc.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export function useUnarchivePalletSize() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const resp = await api.post(`/organization/pallet-sizes/${id}/unarchive`);
      return resp.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.palletSizes });
      qc.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export function useListCustomerLocations(search = '') {
  return useInfiniteQuery({
    queryKey: ['loadOptionCustomerLocations', search.trim()],

    initialPageParam: 1,

    queryFn: async ({ pageParam = 1 }) => {
      const response = await api.get('/organization/load-options/customer-locations', {
        params: {
          search: search.trim() || undefined,

          page: pageParam,
          pageSize: 50,
        },
      });

      return response.data;
    },

    getNextPageParam: (lastPage: any) => {
      return lastPage.hasMore ? lastPage.nextPage : undefined;
    },
  });
}
